/**
 * Manage Appointments React Query Hooks
 *
 * React Query hooks for fetching and caching the manage appointments list
 * and cancel-flow (can-cancel, OTP, cancel, cancellation reasons).
 */

import type { AxiosRequestConfig } from "axios";
import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import {
  manageAppointmentsService,
  type GetManageAppointmentsParams,
  type ManageAppointmentsListResult,
} from "./manage-appointments.service";
import type {
  ManageAppointmentsFilter,
  CancelAppointmentPayload,
  TrackApplicationStatusPayload,
} from "./types";

// ============================================
// Query Keys
// ============================================

export const manageAppointmentsKeys = {
  all: ["manage-appointments"] as const,
  list: (filter: ManageAppointmentsFilter) =>
    [...manageAppointmentsKeys.all, "list", filter] as const,
  cancelReasons: () => [...manageAppointmentsKeys.all, "cancel-reasons"] as const,
};

const DEFAULT_LIMIT = 10;

function filterToStatus(filter: ManageAppointmentsFilter): string | undefined {
  if (filter === "all") return undefined;
  if (filter === "upcoming") return "BOOKED";
  if (filter === "completed") return "COMPLETED";
  if (filter === "cancelled") return "CANCELLED";
  return undefined;
}

// ============================================
// Hooks
// ============================================

export interface UseManageAppointmentsInfiniteParams {
  filter: ManageAppointmentsFilter;
}

/**
 * Fetch manage appointments list with infinite scroll.
 * GET /v1/appointments/manage?page=&limit=
 *
 * Uses useInfiniteQuery; pages are appended as user scrolls.
 */
export function useManageAppointmentsInfinite({
  filter,
}: UseManageAppointmentsInfiniteParams) {
  const status = filterToStatus(filter);

  return useInfiniteQuery({
    queryKey: manageAppointmentsKeys.list(filter),
    queryFn: async ({ pageParam }) => {
      const params: GetManageAppointmentsParams = {
        page: pageParam,
        limit: DEFAULT_LIMIT,
        ...(status ? { status } : {}),
      };
      return manageAppointmentsService.getManageAppointments(params);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { meta } = lastPage;
      return meta.currentPage < meta.totalPages ? meta.currentPage + 1 : undefined;
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchOnMount: "always", // Refetch when landing on the page so new appointments appear without reload
  });
}

// ============================================
// Cancellation reasons (reference data)
// ============================================

/**
 * Fetch cancellation reasons for the cancel flow.
 * GET /v1/reference-data?sources=appointment_cancellation_reasons
 * Enabled when dialog is open so step 3 has options ready.
 */
export function useCancellationReasons(enabled: boolean = true) {
  return useQuery({
    queryKey: manageAppointmentsKeys.cancelReasons(),
    queryFn: () => manageAppointmentsService.getCancellationReasons(),
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000,
  });
}

// ============================================
// Cancel flow mutations
// ============================================

/**
 * Check if appointment can be cancelled.
 * POST /appointments/can-cancel
 * Use onSuccess to branch: if !data.success show not-available; if success call send OTP then go to step 2.
 */
export function useCheckCanCancelMutation() {
  return useMutation({
    mutationFn: ({
      appointmentNumber,
      email,
    }: {
      appointmentNumber: string;
      email: string;
    }) => manageAppointmentsService.checkCanCancel(appointmentNumber, email),
  });
}

/**
 * Send cancellation OTP.
 * POST /otp/appointment/send with otpPurpose CANCELLATION
 * Used after can-cancel success and for Resend OTP.
 */
export function useSendCancellationOtpMutation() {
  return useMutation({
    mutationFn: ({ appointmentId, config }: { appointmentId: number; config?: AxiosRequestConfig }) =>
      manageAppointmentsService.sendCancellationOtp(appointmentId, config),
  });
}

/**
 * Verify cancellation OTP.
 * POST /otp/appointment/verify with otpPurpose CANCELLATION
 * On success move to step 3.
 */
export function useVerifyCancellationOtpMutation() {
  return useMutation({
    mutationFn: ({
      appointmentId,
      otp,
      config,
    }: {
      appointmentId: number;
      otp: string;
      config?: AxiosRequestConfig;
    }) =>
      manageAppointmentsService.verifyCancellationOtp(appointmentId, otp, config),
  });
}

/**
 * Cancel the appointment.
 * POST /appointments/:id/cancel
 * On success invalidate manage appointments list and move to step 4.
 */
export function useCancelAppointmentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      payload,
    }: {
      appointmentId: number;
      payload: CancelAppointmentPayload;
    }) =>
      manageAppointmentsService.cancelAppointment(appointmentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: manageAppointmentsKeys.all });
    },
  });
}

/**
 * Delete a draft appointment.
 * DELETE /appointments/:id
 * Only allowed when status is DRAFT. On success removes the item from the "all" list cache (no refetch).
 */
export function useDeleteDraftAppointmentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (appointmentId: number) =>
      manageAppointmentsService.deleteDraftAppointment(appointmentId),
    onSuccess: (_, appointmentId) => {
      queryClient.setQueryData<InfiniteData<ManageAppointmentsListResult>>(
        manageAppointmentsKeys.list("all"),
        (old) => {
          if (!old) return old;
          let removed = false;
          const pages = old.pages.map((page) => {
            const data = page.data.filter((item) => item.id !== appointmentId);
            if (data.length !== page.data.length) removed = true;
            return { ...page, data };
          });
          if (!removed) return old;
          const newTotal = Math.max(0, (old.pages[0]?.meta.totalItems ?? 1) - 1);
          return {
            ...old,
            pages: pages.map((p) => ({
              ...p,
              meta: { ...p.meta, totalItems: newTotal },
            })),
          };
        }
      );
    },
  });
}

/**
 * Track application status.
 * POST /visa-application/track-status with applicationReferenceNumber and dateOfBirth.
 * @param config - Optional Axios config (e.g. reCAPTCHA headers via recaptchaHeaders(token)).
 */
export function useTrackApplicationStatusMutation() {
  return useMutation({
    mutationFn: ({
      payload,
      config,
    }: {
      payload: TrackApplicationStatusPayload;
      config?: AxiosRequestConfig;
    }) => manageAppointmentsService.trackApplicationStatus(payload, config),
  });
}

/** Params for get-checklist mutation. rawId is optional, for per-card loading UI (e.g. manage list). */
export interface GetChecklistMutationParams {
  countryId: number;
  visaTypeId: number;
  visaSubTypeId: number;
  rawId?: number;
  centerId?: number;
  centerType?: "hub" | "spoke" | "sub_country_hub";
}

/**
 * Fetch checklist URL for document checklist.
 * GET /v1/appointments/checklist?countryId=&visaTypeId=&visaSubTypeId=&centerId=&centerType=
 * Use isPending for loading state; handle onSuccess (open URL) and onError (toast) in the component.
 * When multiple cards use this (e.g. manage list), pass rawId so loading state can be shown per card (variables.rawId).
 */
export function useGetChecklistMutation() {
  return useMutation({
    mutationFn: ({
      countryId,
      visaTypeId,
      visaSubTypeId,
      centerId,
      centerType,
    }: GetChecklistMutationParams) =>
      manageAppointmentsService.getChecklist(
        countryId,
        visaTypeId,
        visaSubTypeId,
        centerId,
        centerType
      ),
  });
}

  // -- NEW FAMILY CASE --
  import {
    getCancellableParticipants,
    cancelParticipants,
  } from "./manage-appointments.service";

  // Types for participant cancellation
  export interface ParticipantCancelPayload {
    participantProfileIds: number[];
    cancellationReasonId: number;
    cancellationAdditionalComments?: string;
  }

  export interface CancellableParticipant {
    participantProfileId: number;
    participantNumber: number | null;
    firstName: string;
    surname: string;
    fullName: string;
    relationshipWithPrimaryApplicant: string | null;
    isPrimary: boolean;
    passportNumber: string | null;
    slotId: number | null;
    slotStartTime: string | null;
    slotEndTime: string | null;
    appointmentDateTime: string | null;
  }

  export interface CancellableParticipantsResponse {
    participants: CancellableParticipant[];
    hasSplitSlots: boolean;
    isFamilyAppointment: boolean;
    primaryParticipantProfileId: number | null;
  }

  /**
   * Fetch cancellable participants for an appointment
   * GET /appointments/:id/cancellable-participants
   * Used in the cancellation flow to show which participants can be cancelled
   */
  export const useGetCancellableParticipants = (
    appointmentId: number,
    enabled: boolean = true
  ) => {
    return useQuery({
      queryKey: ["cancellable-participants", appointmentId],
      queryFn: () => getCancellableParticipants(appointmentId),
      enabled: enabled && !!appointmentId,
      staleTime: 30 * 1000, // 30 seconds
    });
  };

  /**
   * Cancel specific participants from an appointment
   * POST /appointments/:id/participants/cancel
   * On success invalidates manage appointments list
   */
  export const useCancelParticipantsMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: ({
        appointmentId,
        payload,
      }: {
        appointmentId: number;
        payload: ParticipantCancelPayload;
      }) => cancelParticipants(appointmentId, payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: manageAppointmentsKeys.all });
      },
    });
  };
  // -- NEW FAMILY CASE --